import { memo, useEffect, useMemo, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { functions } from '../config/firebase.js'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import LoadingSpinner from '../components/LoadingSpinner.jsx'
import AnalysisPage from '@express/components/pages/AnalysisPage.jsx'

/**
 * Cloud Analysis page - React 19+ optimized Firebase Functions API proxy
 * 
 * Architecture Pattern: 100% Pure Transformation (same as Dashboard.jsx)
 * - memo component with Firebase Functions
 * - memo component with Firebase Functions
 * - fetch interceptor for API routing  
 * - direct AnalysisPage.jsx rendering (no wrapper)
 * - zero express modifications required
 * - zero cloud-specific hooks (pure fetch interception only)
 */
const Analysis = memo(() => {
  const { selectedDeviceId, hasDevices, devicesLoading } = useCloudContext()
  
  // Get the QueryClient from the parent provider
  const queryClient = useQueryClient()
  
  // Track when fetch interceptor is ready
  const [interceptorReady, setInterceptorReady] = useState(false)
  
  // Track device connection status
  const [deviceNotConnected, setDeviceNotConnected] = useState(false)
  const [checkingConnection, setCheckingConnection] = useState(false)
  
  // Memoized Firebase Functions - stable reference prevents re-renders
  const firebaseFunctions = useMemo(() => Object.freeze({
    getHistoricalData: httpsCallable(functions, 'getHistoricalData'),
    getRealTimeData: httpsCallable(functions, 'getRealTimeData'),
    getAllDevicesMeasurements: httpsCallable(functions, 'getAllDevicesMeasurements'),
    getHistoricalFaults: httpsCallable(functions, 'getHistoricalFaults'),
    getHistoricalConfigs: httpsCallable(functions, 'getHistoricalConfigs'),
    exportCombined: httpsCallable(functions, 'exportCombined'),
    getEarliestDate: httpsCallable(functions, 'getEarliestDate'),
  }), [])

  // Check if device has recent data (connection check)
  const checkDeviceConnection = useMemo(() => async () => {
    if (!selectedDeviceId) return
    
    setCheckingConnection(true)
    try {
      // Check for any recent data using getAllDevicesMeasurements
      // This gives us a quick way to see if device has any data at all
      const result = await firebaseFunctions.getAllDevicesMeasurements({ 
        serialNumber: selectedDeviceId,
      })
      
      // If no measurements returned, device hasn't sent any data yet
      const hasAnyData = result.data?.success && Array.isArray(result.data.combinations) && result.data.combinations.length > 0
      setDeviceNotConnected(!hasAnyData)
    } catch (error) {
      console.error('Device connection check failed:', error)
      setDeviceNotConnected(true)
    } finally {
      setCheckingConnection(false)
    }
  }, [selectedDeviceId, firebaseFunctions])

  // Check device connection when device changes
  useEffect(() => {
    if (selectedDeviceId && interceptorReady) {
      checkDeviceConnection()
    }
  }, [selectedDeviceId, interceptorReady, checkDeviceConnection])

  // Reset connection status when device changes
  useEffect(() => {
    setDeviceNotConnected(false)
    setCheckingConnection(false)
  }, [selectedDeviceId])

  // Streamlined fetch interceptor with analysis-specific routing
  useEffect(() => {
    if (!selectedDeviceId) {
      setInterceptorReady(false)
      return
    }
    
    const originalFetch = globalThis.fetch
    const apiRoutes = new Map([
      // More specific patterns first to avoid conflicts
      
      // Device Measurements - GET /api/all-devices-measurements (with optional query params)
      ['/api/all-devices-measurements?', async () => {
        const result = await firebaseFunctions.getAllDevicesMeasurements({ serialNumber: selectedDeviceId })
        const combinations = result.data?.success ? result.data.combinations : []
        
        // Update device connection status based on data availability
        const hasAnyData = Array.isArray(combinations) && combinations.length > 0
        setDeviceNotConnected(!hasAnyData)
        
        return combinations
      }],
      ['/api/all-devices-measurements', async () => {
        const result = await firebaseFunctions.getAllDevicesMeasurements({ serialNumber: selectedDeviceId })
        const combinations = result.data?.success ? result.data.combinations : []
        
        // Update device connection status based on data availability
        const hasAnyData = Array.isArray(combinations) && combinations.length > 0
        setDeviceNotConnected(!hasAnyData)
        
        return combinations
      }],
      
      // Historical Faults - GET /api/historical-faults?start=&end= (must be before /api/historical?)
      ['/api/historical-faults?', async urlString => {
        const params = new URLSearchParams(urlString.split('?')[1])
        const start = params.get('start')
        const end = params.get('end')
        
        // Return empty data if required parameters are missing (graceful degradation)
        if (!start || !end) {
          return []
        }
        
        const result = await firebaseFunctions.getHistoricalFaults({
          start,
          end,
          serialNumber: selectedDeviceId,
        })
        return result.data?.success ? result.data.faults : []
      }],
      
      // Historical Configs - GET /api/historical-configs?start=&end= (must be before /api/historical?)
      ['/api/historical-configs?', async urlString => {
        const params = new URLSearchParams(urlString.split('?')[1])
        const start = params.get('start')
        const end = params.get('end')
        
        // Return empty data if required parameters are missing (graceful degradation)
        if (!start || !end) {
          return []
        }
        
        const result = await firebaseFunctions.getHistoricalConfigs({
          start,
          end,
          serialNumber: selectedDeviceId,
        })
        return result.data?.success ? result.data.configs : []
      }],

      // Historical Data - GET /api/historical?device=&measurement=&start=&end= (must be after more specific patterns)
      ['/api/historical?', async urlString => {
        const params = new URLSearchParams(urlString.split('?')[1])
        const device = params.get('device')
        const measurement = params.get('measurement')
        const start = params.get('start')
        const end = params.get('end')
        
        // Return empty data if required parameters are missing (graceful degradation)
        if (!device || !measurement) {
          return []
        }
        
        const result = await firebaseFunctions.getHistoricalData({
          serialNumber: selectedDeviceId,
          device,
          measurement,
          start,
          end,
        })
        const data = result.data?.success ? result.data.data : []
        return data
      }],
      
      // Export Combined Data - GET /api/export-combined?devices=&measurements=&start=&end=
      ['/api/export-combined?', async urlString => {
        const params = new URLSearchParams(urlString.split('?')[1])
        const result = await firebaseFunctions.exportCombined({
          devices: params.get('devices')?.split(',') ?? [],
          measurements: params.get('measurements')?.split(',') ?? [],
          start: params.get('start'),
          end: params.get('end'),
        })
        const csvData = result.data?.success ? result.data.csvData : ''
        return new Blob([csvData], { type: 'text/csv' })
      }],

      // Earliest Date - GET /api/earliest-date
      ['/api/earliest-date', async () => {
        try {
          const result = await firebaseFunctions.getEarliestDate({ serialNumber: selectedDeviceId })
          
          if (result.data?.success && result.data?.earliestDate) {
            return { 
              earliestDate: result.data.earliestDate,
              fallback: false,
            }
          }
          
          // Fallback to 30 days ago if no data found
          const thirtyDaysAgo = new Date()
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
          
          return { 
            earliestDate: thirtyDaysAgo.toISOString(),
            fallback: true,
          }
        } catch (error) {
          // Fallback to 30 days ago on error
          const thirtyDaysAgo = new Date()
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
          
          return { 
            earliestDate: thirtyDaysAgo.toISOString(),
            fallback: true,
            error: error.message,
          }
        }
      }],

      // Dashboard Fields - GET /api/dashboard-fields (express components sometimes call this)
      ['/api/dashboard-fields', async () => {
        // Return empty dashboard fields - this prevents 404 errors when dashboard components are used
        return []
      }],
      
      // Faults - GET /faults (express components sometimes call this)
      ['/faults', async () => {
        // Return empty faults data
        return []
      }],

      // Real-Time Data - GET /api (must be last to avoid conflicts)
      ['/api', async () => {
        const result = await firebaseFunctions.getRealTimeData({ serialNumber: selectedDeviceId })
        const data = result.data?.success ? result.data.data : []
        
        // If data is empty, return a special marker that won't trigger express handleResponse empty conversion
        if (Array.isArray(data) && data.length === 0) {
          // Return an array with a special empty marker that gets filtered out by express components
          return [{ _isEmpty: true }]
        }
        return data
      }],
    ])

    globalThis.fetch = async (input, options) => {
      try {
        // Handle both URL strings and Request objects more robustly
        let urlString = ''
        if (typeof input === 'string') {
          urlString = input
        } else if (input instanceof URL) {
          urlString = input.pathname + input.search
        } else if (input?.url) {
          urlString = input.url
        } else {
          urlString = String(input)
        }
        
        // Only intercept our analysis API routes, let Firebase requests pass through
        if (urlString.startsWith('/api')) {
          for (const [pattern, handler] of apiRoutes) {
            try {
              // Improved pattern matching (same as Dashboard.jsx)
              let shouldIntercept = false
              if (pattern.endsWith('?')) {
                // Pattern like '/api/historical?' should match '/api/historical?device=...'
                shouldIntercept = urlString.startsWith(pattern.slice(0, -1))
              } else {
                // Exact match for patterns like '/api', '/api/all-devices-measurements'
                shouldIntercept = urlString === pattern
              }
              
              if (shouldIntercept) {
                try {
                  const data = await handler(urlString)
                  const isBlob = data instanceof Blob
                  return {
                    ok: true,
                    status: 200,
                    statusText: 'OK',
                    headers: new Headers({ 'content-type': isBlob ? 'text/csv' : 'application/json' }),
                    json: async () => isBlob ? {} : data,
                    text: async () => isBlob ? await data.text() : JSON.stringify(data),
                    blob: async () => isBlob ? data : new Blob([JSON.stringify(data)], { type: 'application/json' }),
                  }
                } catch (handlerError) {
                  console.error('Firebase handler error:', handlerError.message)
                  return {
                    ok: false,
                    status: 500,
                    statusText: 'Internal Server Error',
                    headers: new Headers({ 'content-type': 'application/json' }),
                    json: async () => ({ error: handlerError.message, pattern, url: urlString }),
                    text: async () => JSON.stringify({ error: handlerError.message, pattern, url: urlString }),
                    blob: async () => new Blob([JSON.stringify({ error: handlerError.message, pattern, url: urlString })], { type: 'application/json' }),
                  }
                }
              }
            } catch (error) {
              console.error('❌ Error in pattern matching for', pattern, ':', error)
              // If there's an error in a handler and this pattern should have been intercepted
              let errorShouldIntercept = false
              if (pattern.endsWith('?')) {
                errorShouldIntercept = urlString.startsWith(pattern.slice(0, -1))
              } else {
                errorShouldIntercept = urlString === pattern
              }
              
              if (errorShouldIntercept) {
                return {
                  ok: false,
                  status: 400,
                  statusText: 'Bad Request',
                  headers: new Headers({ 'content-type': 'application/json' }),
                  json: async () => ({ error: error.message }),
                  text: async () => JSON.stringify({ error: error.message }),
                  blob: async () => new Blob([JSON.stringify({ error: error.message })], { type: 'application/json' }),
                }
              }
            }
          }
          
          // Catch-all for any remaining API routes to prevent 404s
          if (urlString.startsWith('/api/')) {
            return {
              ok: true,
              status: 200,
              statusText: 'OK',
              headers: new Headers({ 'content-type': 'application/json' }),
              json: async () => [],
              text: async () => JSON.stringify([]),
              blob: async () => new Blob([JSON.stringify([])], { type: 'application/json' }),
            }
          }
          
          // If we reach here, no pattern matched the API call
          return {
            ok: false,
            status: 404,
            statusText: 'Not Found',
            headers: new Headers({ 'content-type': 'application/json' }),
            json: async () => ({ error: 'API endpoint not found in fetch interceptor', url: urlString }),
            text: async () => JSON.stringify({ error: 'API endpoint not found in fetch interceptor', url: urlString }),
            blob: async () => new Blob([JSON.stringify({ error: 'API endpoint not found in fetch interceptor', url: urlString })], { type: 'application/json' }),
          }
        }
        
        return originalFetch(input, options)
      } catch (error) {
        console.error(`Firebase API proxy error for ${typeof input === 'string' ? input : input?.url}:`, error)
        return {
          ok: false,
          status: 500,
          statusText: 'Internal Server Error',
          headers: new Headers({ 'content-type': 'application/json' }),
          json: async () => ({ error: error.message }),
          text: async () => JSON.stringify({ error: error.message }),
          blob: async () => new Blob([JSON.stringify({ error: error.message })], { type: 'application/json' }),
        }
      }
    }
    
    // Mark interceptor as ready and setup cleanup
    setInterceptorReady(true)
    
    return () => { 
      globalThis.fetch = originalFetch
      setInterceptorReady(false)
    }
  }, [selectedDeviceId, firebaseFunctions])

  // Early returns with consistent loading states
  if (devicesLoading) return <LoadingSpinner message="Loading device data..." />
  
  if (!hasDevices) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-graph-up" style={{ fontSize: '4rem', color: 'var(--text-light-muted)' }} />
        <h4 className="mt-3 mb-3">No Devices to Analyze</h4>
        <p className="text-muted">Add a device to start viewing historical data and trends.</p>
      </div>
    )
  }
  
  if (!selectedDeviceId) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2" />
        Please select a device to view its analysis data.
      </div>
    )
  }
  
  // Wait for fetch interceptor to be ready before rendering analysis
  if (!interceptorReady) {
    return <LoadingSpinner message="Setting up analysis..." />
  }
  
  // Show connection checking state
  if (checkingConnection) {
    return <LoadingSpinner message="Checking device connection..." />
  }
  
  // Show device not connected state when device exists but has no historical data
  if (deviceNotConnected) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-wifi-off" style={{ fontSize: '4rem', color: 'var(--text-light-muted)' }} />
        <h4 className="mt-3 mb-3">Device Not Connected</h4>
        <p className="text-muted mb-4">
          The device <strong>{selectedDeviceId}</strong> hasn&apos;t sent any data to the cloud recently.
        </p>
        <div className="text-muted">
          <p className="mb-2"><strong>Troubleshooting:</strong></p>
          <ul className="list-unstyled">
            <li>• Check that the device is powered on</li>
            <li>• Verify internet/cellular connectivity</li>
            <li>• Ensure the device is properly configured</li>
            <li>• Allow time for initial data to be recorded</li>
          </ul>
        </div>
        <button 
          className="btn btn-outline-primary mt-3"
          onClick={() => {
            checkDeviceConnection()
          }}
        >
          <i className="bi bi-arrow-clockwise me-2" />
          Check Connection Again
        </button>
      </div>
    )
  }
  
  // Wrap the express AnalysisPage with QueryClientProvider to ensure it has access to React Query
  return (
    <QueryClientProvider client={queryClient}>
      <AnalysisPage />
    </QueryClientProvider>
  )
})

Analysis.displayName = 'Analysis'

export default Analysis
